import React, { useEffect, useState } from 'react';
import { Percent, ShieldCheck, RefreshCw, Save } from 'lucide-react';
import { useAccessControl } from '../App';

const DEFAULT_CONFIG = {
  enabled: true,
  rate: 7.5,
  name: 'VAT',
  description: 'VAT is centrally applied at checkout by Revstar for compliance. Merchants remain responsible for remittance.',
  effectiveFrom: '',
};

export default function CentralVatManagement() {
  const { apiWithAccessControl } = useAccessControl();
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const loadConfig = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await apiWithAccessControl('/admin/tax/vat');
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to load VAT settings');
      setConfig({ ...DEFAULT_CONFIG, ...(data.config || {}) });
    } catch (err) {
      setError(err.message || 'Failed to load VAT settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfig();
  }, []);

  const saveConfig = async () => {
    try {
      setSaving(true);
      setMessage('');
      setError('');
      const res = await apiWithAccessControl('/admin/tax/vat', {
        method: 'PUT',
        body: JSON.stringify({
          enabled: config.enabled,
          rate: Number(config.rate),
          name: config.name,
          description: config.description,
          effectiveFrom: config.effectiveFrom || null,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to save VAT settings');
      setConfig({ ...DEFAULT_CONFIG, ...(data.config || {}) });
      setMessage('Central VAT settings saved.');
    } catch (err) {
      setError(err.message || 'Failed to save VAT settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={{ padding: 24, color: '#64748b' }}>Loading VAT settings...</div>;
  }

  return (
    <div style={{ padding: 24, maxWidth: 980 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, color: '#0f172a', fontSize: 28, fontWeight: 800 }}>Central VAT</h1>
          <p style={{ margin: '8px 0 0', color: '#64748b', maxWidth: 680 }}>
            Enforce the VAT line applied across storefront checkout, WhatsApp orders, POS, and appointment payments.
          </p>
        </div>
        <button
          type="button"
          onClick={loadConfig}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 14px', border: '1px solid #cbd5e1', borderRadius: 8, background: '#fff', color: '#0f172a', cursor: 'pointer' }}
        >
          <RefreshCw size={16} /> Refresh
        </button>
      </div>

      {message && <div style={{ marginBottom: 16, padding: 12, borderRadius: 8, background: '#ecfdf5', color: '#047857' }}>{message}</div>}
      {error && <div style={{ marginBottom: 16, padding: 12, borderRadius: 8, background: '#fef2f2', color: '#b91c1c' }}>{error}</div>}

      <section style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 20, boxShadow: '0 1px 2px rgba(15, 23, 42, 0.05)' }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 18 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: '#ecfdf5', display: 'grid', placeItems: 'center', color: '#0d7463' }}>
            <ShieldCheck size={22} />
          </div>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, color: '#0f172a' }}>Compliance source of truth</h2>
            <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 14 }}>Merchants can view this setting, but cannot override it. They remain responsible for VAT remittance.</p>
          </div>
        </div>

        <div style={{ display: 'grid', gap: 16 }}>
          <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, padding: '14px 0', borderTop: '1px solid #e2e8f0' }}>
            <span>
              <strong style={{ display: 'block', color: '#0f172a' }}>Apply VAT</strong>
              <small style={{ color: '#64748b' }}>Turns central VAT application on or off platform-wide.</small>
            </span>
            <input
              type="checkbox"
              checked={!!config.enabled}
              onChange={(e) => setConfig((prev) => ({ ...prev, enabled: e.target.checked }))}
            />
          </label>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
            <label>
              <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#334155', marginBottom: 6 }}>Tax name</span>
              <input
                value={config.name || ''}
                onChange={(e) => setConfig((prev) => ({ ...prev, name: e.target.value }))}
                style={{ width: '100%', padding: '11px 12px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 14 }}
                placeholder="VAT"
              />
            </label>
            <label>
              <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#334155', marginBottom: 6 }}>Rate (%)</span>
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={config.rate}
                onChange={(e) => setConfig((prev) => ({ ...prev, rate: e.target.value }))}
                style={{ width: '100%', padding: '11px 12px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 14 }}
                placeholder="7.5"
              />
            </label>
          </div>

          <label>
            <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#334155', marginBottom: 6 }}>Merchant-facing note</span>
            <textarea
              value={config.description || ''}
              onChange={(e) => setConfig((prev) => ({ ...prev, description: e.target.value }))}
              rows={3}
              style={{ width: '100%', padding: '11px 12px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 14, resize: 'vertical' }}
            />
          </label>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#0d7463', background: '#f0fdfa', border: '1px dashed #99f6e4', borderRadius: 10, padding: 12 }}>
            <Percent size={16} />
            <span style={{ fontSize: 14 }}>
              A ₦10,000 taxable subtotal will add ₦{(10000 * ((Number(config.rate) || 0) / 100)).toFixed(2)} {config.name || 'VAT'}.
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
          <button
            type="button"
            disabled={saving}
            onClick={saveConfig}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '11px 18px', border: 0, borderRadius: 8, background: saving ? '#94a3b8' : '#0d7463', color: '#fff', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer' }}
          >
            <Save size={16} /> {saving ? 'Saving...' : 'Save VAT settings'}
          </button>
        </div>
      </section>
    </div>
  );
}
